import { AlertVariant, Button, Card, CardBody, Flex, FlexItem } from '@patternfly/react-core';
import React, { useCallback, useEffect, useState } from 'react';
import { TWidget } from '../../api/dashbboards/DashboardWidgets';
import PresentationSlide from './PresentationSlide';
import { useMount } from 'react-use';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';
import { Present } from '../../api/present/Present';
import { addNewRecentPresentation } from '../../helpers/helper-components/recents-factory-helper';
import { TNewDateRange } from '../../api/types/TNewDateRange';
import { DashboardFilter } from '../../api/dashbboards/DashboardFilter';
import { TPresentationSlide } from '../../api/present/PresentationSlide';
import { useParams } from 'react-router';
import Loader from '../../components/util/Loader';
import DatePeriodSelector from '../../components/date-period-selector/DatePeriodSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faChevronLeft,
	faChevronRight,
	faCompress,
	faExpand,
} from '@fortawesome/pro-light-svg-icons';

const PresentView = () => {
	const { addToast } = useToast();
	const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
	const [selectedSlide, setSelectedSlide] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(false);
	const presentationModel: Present = Present.Default();
	const { presentationId } = useParams();
	const [pageModel, setPageModel] = useState<Present>(presentationModel);
	const [selectedDate, setSelectedDate] = useState<TNewDateRange>();
	const [dashboardFilters, setDashboardFilters] = useState<DashboardFilter[] | undefined>([]);
	const updateSelectedDate = useCallback(
		(startDateRange: TNewDateRange, endDateRange: TNewDateRange) => {
			if (setSelectedDate) {
				setSelectedDate({
					begin_date: startDateRange.begin_date,
					end_date: endDateRange.end_date,
					period: startDateRange.period,
					sequence: startDateRange.sequence,
				});
			}
		},
		[selectedDate, setSelectedDate]
	);

	useMount(() => {
		getPresentationData();
	});

	const _handleKeyDown = (event: KeyboardEvent) => {
		switch (event.code) {
			case 'ArrowLeft': // Left arrow
				if (selectedSlide > 0) setSelectedSlide(selectedSlide - 1);
				break;
			case 'ArrowRight': // Right arrow
			case 'Space': // Spacebar
				if (selectedSlide < pageModel.presentationSlides.length - 1)
					setSelectedSlide(selectedSlide + 1);
				break;
			default:
				break;
		}
	};
	removeEventListener('keydown', _handleKeyDown);
	addEventListener('keydown', _handleKeyDown);
	useEffect(() => {
		return () => {
			document.removeEventListener('keydown', _handleKeyDown);
		};
	}, []);

	const getPresentationData = () => {
		if (presentationId) {
			setLoading(true);
			let filterRequest = undefined;
			if (presentationId) {
				filterRequest = { presentation: presentationId.toString() };
			}
			if (filterRequest) {
				DashboardFilter.GetAll(filterRequest)
					.then((dashboardFilters) => {
						setDashboardFilters(dashboardFilters);
						updateFilters(dashboardFilters);
						Present.Get(parseInt(presentationId), ['presentationSlides'])
							.then((presentationResponse) => {
								if (presentationResponse) {
									// setCurrentSelectedPresentation(presentationResponse);
									const newModel = {
										...presentationModel,
										...presentationResponse,
									};

									const promiseAll: Promise<TPresentationSlide>[] = [];
									presentationResponse.presentationSlides.forEach((slide) => {
										if (typeof slide != 'number' && slide.id)
											promiseAll.push(
												Present.GetPresentationSlide(Number(slide.id), [
													'widgets',
												])
											);
									});

									Promise.all(promiseAll)
										.then((responses: TPresentationSlide[]) => {
											responses.forEach((slideResponse) => {
												const presentSlide =
													newModel.presentationSlides.find(
														(slide) => slide.id === slideResponse.id
													) as TPresentationSlide;
												presentSlide.widgets = (
													slideResponse.widgets as TWidget[]
												).map((w) => {
													return {
														...w,
														presentationSlide: presentSlide.id,
													};
												});
											});
											setPageModel(newModel);
										})
										.catch((_) => {
											addToast('Failed to get Presentation Slides');
										})
										.finally(() => setLoading(false));
								}
							})
							.catch(() => {
								addToast('Get presentation failed.', AlertVariant.danger);
							});
					})
					.catch((): void => {
						addToast('Error loading filters.', AlertVariant.danger);
					});
			}

			//log entry into recents for loaded presentation
			addNewRecentPresentation(presentationId);
		}
	};

	const updateFilters = (filters: DashboardFilter[]) => {
		setDashboardFilters(filters);
	};

	const movePreviousSlide = () => {
		const newSlideNumber = selectedSlide - 1;
		setSelectedSlide(newSlideNumber);
	};

	const moveNextSlide = () => {
		const newSlideNumber = selectedSlide + 1;
		setSelectedSlide(newSlideNumber);
	};

	const toggleFullscreen = () => {
		const doc = window.document;
		const docEl = doc.documentElement;

		// eslint-disable-next-line @typescript-eslint/unbound-method
		const requestFullScreen = docEl.requestFullscreen;
		// eslint-disable-next-line @typescript-eslint/unbound-method
		const cancelFullScreen = doc.exitFullscreen;

		if (!doc.fullscreenElement) {
			// eslint-disable-next-line @typescript-eslint/unbound-method
			requestFullScreen.call(docEl).catch((e) => console.log(e));
			setIsFullScreen(true);
		} else {
			// eslint-disable-next-line @typescript-eslint/unbound-method
			cancelFullScreen.call(doc).catch((e) => console.log(e));
			setIsFullScreen(false);
		}
	};

	return (
		<>
			{loading && <Loader isFullScreen />}
			<div style={{ display: 'none' }}>
				<DatePeriodSelector applyDateRange={updateSelectedDate} />
			</div>
			<div
				style={{
					height: '100vh',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					position: 'relative',
				}}
			>
				<PresentationSlide
					slide={pageModel.presentationSlides[selectedSlide]}
					filters={dashboardFilters}
					selectedDate={selectedDate}
					isEdit={false}
					allowClickNavigate={false}
					noAnimation={true}
					displayOnly
				/>
				<Card
					isCompact
					isRounded
					style={{
						margin: '0 auto',
						position: 'absolute',
						bottom: '0px',
						alignSelf: 'center',
					}}
				>
					<CardBody style={{ padding: '0.2rem' }}>
						<Flex justifyContent={{ default: 'justifyContentCenter' }}>
							<FlexItem>
								<Button
									variant="link"
									isDisabled={selectedSlide === 0}
									onClick={() => movePreviousSlide()}
								>
									<FontAwesomeIcon icon={faChevronLeft} />
								</Button>
							</FlexItem>
							<FlexItem>
								<Button
									variant="link"
									onClick={() => toggleFullscreen()}
								>
									{isFullScreen ? (
										<FontAwesomeIcon icon={faCompress} />
									) : (
										<FontAwesomeIcon icon={faExpand} />
									)}
								</Button>
							</FlexItem>
							<FlexItem>
								<Button
									variant="link"
									isDisabled={
										selectedSlide === pageModel.presentationSlides.length - 1
									}
									onClick={() => moveNextSlide()}
								>
									<FontAwesomeIcon icon={faChevronRight} />
								</Button>
							</FlexItem>
						</Flex>
					</CardBody>
				</Card>
			</div>
		</>
	);
};

export default PresentView;
